import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Vista admin per la modifica dei modelli email usati nel workflow di approvazione
 * iscrizioni extracurriculari (approvazione + messa in lista d'attesa).
 * Backend: GET/PUT/DELETE /api/mensa-matrice/email-templates[/{tid}]
 */
export default function EmailTemplatesView() {
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // { id, subject, body, default_subject, default_body, is_builtin? }
  const [creating, setCreating] = useState(null); // { id, label, subject, body } for new template
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  // Configurazione email globale (mittente + coordinate pagamento)
  const [cfg, setCfg] = useState({
    from_email: "", from_name: "", reply_to: "",
    coordinate_pagamento: "", causale_default: "",
    resend_configured: false, sender_env: "",
  });
  const [cfgDirty, setCfgDirty] = useState(false);
  const [cfgSaving, setCfgSaving] = useState(false);

  const carica = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/mensa-matrice/email-templates"),
        api.get("/mensa-matrice/email-config"),
      ]);
      setItems(r1.data.items || []);
      setPlaceholders(r1.data.placeholders || []);
      setCfg({
        from_email: r2.data.from_email || "",
        from_name: r2.data.from_name || "",
        reply_to: r2.data.reply_to || "",
        coordinate_pagamento: r2.data.coordinate_pagamento || "",
        causale_default: r2.data.causale_default || "",
        resend_configured: !!r2.data.resend_configured,
        sender_env: r2.data.sender_env || "",
      });
      setCfgDirty(false);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { carica(); }, []);

  const salvaCfg = async () => {
    setCfgSaving(true); setErr(null);
    try {
      await api.put("/mensa-matrice/email-config", cfg);
      setCfgDirty(false);
      setToast({ msg: "Configurazione salvata", type: "success" });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio configurazione");
    } finally { setCfgSaving(false); }
  };

  const salva = async () => {
    if (!editing) return;
    setSaving(true); setErr(null);
    try {
      await api.put(`/mensa-matrice/email-templates/${editing.id}`, {
        subject: editing.subject, body: editing.body,
      });
      setToast({ msg: "Modello salvato", type: "success" });
      setEditing(null);
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio modello");
    } finally { setSaving(false); }
    setTimeout(() => setToast(null), 3000);
  };

  const resetDefault = async (tid) => {
    if (!window.confirm("Ripristinare il modello ai valori di default? La tua personalizzazione andrà persa.")) return;
    try {
      await api.delete(`/mensa-matrice/email-templates/${tid}`);
      await carica();
      setToast({ msg: "Modello ripristinato ai default", type: "success" });
      setTimeout(() => setToast(null), 3000);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const eliminaCustom = async (tid, label) => {
    if (!window.confirm(`Eliminare definitivamente il modello "${label}"? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/mensa-matrice/email-templates/${tid}`);
      await carica();
      setToast({ msg: "Modello eliminato", type: "success" });
      setTimeout(() => setToast(null), 3000);
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const creaNuovo = async () => {
    if (!creating) return;
    setSaving(true); setErr(null);
    try {
      await api.post("/mensa-matrice/email-templates", {
        id: creating.id, label: creating.label,
        subject: creating.subject, body: creating.body,
      });
      setCreating(null);
      await carica();
      setToast({ msg: "Modello creato", type: "success" });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore creazione modello");
    } finally { setSaving(false); }
  };

  const inserisciPh = (ph) => {
    if (creating) {
      setCreating({ ...creating, body: (creating.body || "") + ph });
      return;
    }
    if (!editing) return;
    setEditing({ ...editing, body: (editing.body || "") + ph });
  };

  return (
    <div style={{ padding: "12px 20px" }}>
      <div style={{ marginBottom: 12, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, color: "#4a148c", fontSize: 20 }}>✉ Modelli Email · Iscrizioni Extra</h2>
          <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
            Modifica gli oggetti e i corpi delle email inviate quando un'iscrizione viene approvata, messa in lista d'attesa o per comunicazioni custom.
          </div>
        </div>
        <button onClick={() => setCreating({ id: "", label: "", subject: "", body: "" })} data-testid="mm-email-new"
          style={{ background: "#8e24aa", color: "white", border: "none", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          + Nuovo modello
        </button>
      </div>

      {err && <div style={{ marginBottom: 12, padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc", borderRadius: 3 }}>{err} <button onClick={() => setErr(null)} style={{ marginLeft: 10, background: "none", border: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}
      {toast && <div style={{ position: "fixed", top: 80, right: 24, background: toast.type === "success" ? "#2e7d32" : "#c62828", color: "white", padding: "10px 18px", borderRadius: 4, zIndex: 1000, fontWeight: 700, fontSize: 13, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>{toast.msg}</div>}

      {loading && <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</div>}

      {!loading && (
        <div data-testid="mm-email-cfg" style={{ background: "white", border: "2px solid #2e7d32", borderRadius: 4, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 22 }}>💳</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1b5e20", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                Coordinate di pagamento &amp; Mittente
                {cfg.resend_configured
                  ? <span style={{ padding: "2px 8px", background: "#1b5e20", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 2 }} title={`Mittente env: ${cfg.sender_env || "—"}`}>✓ RESEND ATTIVO</span>
                  : <span style={{ padding: "2px 8px", background: "#bf360c", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>⚠ RESEND NON CONFIGURATO</span>}
              </div>
              <div style={{ fontSize: 10, color: "#666" }}>
                Questi valori vengono inseriti automaticamente nei modelli email usando i placeholder
                <code style={{ background: "#e8f5e9", padding: "1px 5px", margin: "0 3px", fontSize: 11 }}>{"{coordinate_pagamento}"}</code>
                e
                <code style={{ background: "#e8f5e9", padding: "1px 5px", margin: "0 3px", fontSize: 11 }}>{"{causale}"}</code>.
              </div>
            </div>
            {cfgDirty && (
              <button onClick={salvaCfg} disabled={cfgSaving} data-testid="mm-email-cfg-save"
                style={{ background: "#2e7d32", color: "white", border: "none", padding: "8px 18px", fontSize: 12, fontWeight: 700, cursor: cfgSaving ? "wait" : "pointer", borderRadius: 3 }}>
                {cfgSaving ? "Salvataggio..." : "💾 Salva"}
              </button>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>EMAIL MITTENTE *</label>
              <input value={cfg.from_email} onChange={e => { setCfg({ ...cfg, from_email: e.target.value }); setCfgDirty(true); }}
                placeholder="segreteria@canossadiaz.it" data-testid="mm-email-cfg-from"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", borderRadius: 3, fontSize: 12 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>NOME MITTENTE</label>
              <input value={cfg.from_name} onChange={e => { setCfg({ ...cfg, from_name: e.target.value }); setCfgDirty(true); }}
                placeholder="Istituto Canossiano · Segreteria" data-testid="mm-email-cfg-fromname"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", borderRadius: 3, fontSize: 12 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>REPLY-TO (risposte)</label>
              <input value={cfg.reply_to} onChange={e => { setCfg({ ...cfg, reply_to: e.target.value }); setCfgDirty(true); }}
                placeholder="segreteria@canossadiaz.it" data-testid="mm-email-cfg-replyto"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", borderRadius: 3, fontSize: 12 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CAUSALE STANDARD</label>
              <input value={cfg.causale_default} onChange={e => { setCfg({ ...cfg, causale_default: e.target.value }); setCfgDirty(true); }}
                placeholder="Iscrizione {corso_descrizione} - {nome} {cognome}" data-testid="mm-email-cfg-causale"
                style={{ width: "100%", padding: 7, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace" }} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>COORDINATE DI PAGAMENTO (IBAN, intestatario, BIC...)</label>
              <textarea value={cfg.coordinate_pagamento} onChange={e => { setCfg({ ...cfg, coordinate_pagamento: e.target.value }); setCfgDirty(true); }}
                rows={5} data-testid="mm-email-cfg-coords"
                placeholder={"Intestatario: Istituto Canossiano · Brescia\nIBAN: IT00 X000 0000 0000 0000 0000 000\nBIC/SWIFT: ...\nCausale: {causale}"}
                style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace", resize: "vertical", lineHeight: 1.5 }} />
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <div style={{ background: "white", border: "1px solid #ccc" }}>
          <table className="sw-table sw-table-compact" style={{ width: "100%" }}>
            <thead style={{ background: "#1f3864", color: "white" }}>
              <tr>
                <th style={{ width: 50, textAlign: "center" }}>#</th>
                <th style={{ width: 220 }}>EVENTO</th>
                <th>OGGETTO</th>
                <th style={{ width: 110, textAlign: "center" }}>STATO</th>
                <th style={{ width: 90, textAlign: "center" }}>AZIONI</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t, i) => {
                const isPersonalized = !t.is_default;
                return (
                  <tr key={t.id} data-testid={`mm-email-tpl-${t.id}`} style={{ borderTop: "1px solid #eee" }}>
                    <td style={{ textAlign: "center", color: "#888", fontFamily: "Consolas, monospace" }}>{i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 700, color: "#0d47a1" }}>{t.label}</div>
                      <div style={{ fontSize: 9, color: "#888", fontFamily: "Consolas, monospace" }}>id: {t.id}</div>
                    </td>
                    <td style={{ fontSize: 12, color: "#333" }} title={t.subject}>{t.subject}</td>
                    <td style={{ textAlign: "center" }}>
                      {!t.is_builtin
                        ? <span style={{ padding: "2px 8px", background: "#f3e5f5", color: "#4a148c", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>★ CUSTOM</span>
                        : isPersonalized
                          ? <span style={{ padding: "2px 8px", background: "#e8f5e9", color: "#1b5e20", fontSize: 10, fontWeight: 700, borderRadius: 2 }} title={`Modificato da ${t.updated_by || "?"} il ${t.updated_at || ""}`}>✓ PERSONALIZZATO</span>
                          : <span style={{ padding: "2px 8px", background: "#eceff1", color: "#37474f", fontSize: 10, fontWeight: 700, borderRadius: 2 }}>DEFAULT</span>}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button onClick={() => setEditing({ ...t })} data-testid={`mm-email-edit-${t.id}`}
                        style={{ background: "#1565c0", color: "white", border: "none", padding: "4px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>✏ Modifica</button>
                      {t.is_builtin && isPersonalized && (
                        <button onClick={() => resetDefault(t.id)} data-testid={`mm-email-reset-${t.id}`}
                          title="Ripristina ai valori di default"
                          style={{ background: "white", color: "#c62828", border: "1px solid #c62828", padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>↺</button>
                      )}
                      {!t.is_builtin && (
                        <button onClick={() => eliminaCustom(t.id, t.label)} data-testid={`mm-email-del-${t.id}`}
                          title="Elimina modello custom"
                          style={{ background: "#c62828", color: "white", border: "none", padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div onClick={() => setEditing(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="mm-email-edit-modal"
            style={{ background: "white", width: "100%", maxWidth: 760, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#8e24aa", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>✏ Modifica modello: {editing.label}</strong>
              <button onClick={() => setEditing(null)} data-testid="mm-email-edit-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto" }}>
              <div style={{ marginBottom: 12, padding: 10, background: cfg.resend_configured ? "#e8f5e9" : "#fff3e0", border: `1px solid ${cfg.resend_configured ? "#81c784" : "#ffb74d"}`, borderRadius: 3, fontSize: 11, color: cfg.resend_configured ? "#1b5e20" : "#bf360c", lineHeight: 1.5 }}>
                {cfg.resend_configured
                  ? <>✓ <b>Resend attivo</b>. Le email verranno inviate realmente al destinatario quando userai il tasto "📧 Invia" dalla lista iscrizioni.</>
                  : <>⚠ <b>L'invio email automatico non è ancora attivo</b> (RESEND_API_KEY mancante). Qui puoi comunque salvare i modelli.</>}
              </div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>OGGETTO *</label>
              <input value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })}
                data-testid="mm-email-edit-subject"
                style={{ width: "100%", padding: 8, border: "2px solid #8e24aa", borderRadius: 3, fontSize: 13, fontWeight: 600, marginBottom: 12 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CORPO MESSAGGIO *</label>
              <textarea value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} rows={14}
                data-testid="mm-email-edit-body"
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace", resize: "vertical", lineHeight: 1.5 }} />

              <div style={{ marginTop: 10, padding: 10, background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 3 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#0d47a1", marginBottom: 6 }}>📋 PLACEHOLDER DISPONIBILI (clicca per inserire)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {placeholders.map(ph => (
                    <button key={ph} onClick={() => inserisciPh(ph)} data-testid={`mm-email-ph-${ph}`}
                      style={{ padding: "3px 8px", background: "white", color: "#0d47a1", border: "1px solid #1565c0", fontFamily: "Consolas, monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                      {ph}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <button onClick={() => setEditing({ ...editing, subject: editing.default_subject, body: editing.default_body })}
                data-testid="mm-email-edit-reset"
                style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                ↺ Riempi con default
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setEditing(null)} data-testid="mm-email-edit-cancel"
                  style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                  Annulla
                </button>
                <button onClick={salva} disabled={saving} data-testid="mm-email-edit-save"
                  style={{ background: saving ? "#bbb" : "#8e24aa", color: "white", border: "none", padding: "8px 20px", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
                  {saving ? "Salvataggio..." : "💾 Salva modello"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {creating && (
        <div onClick={() => setCreating(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="mm-email-create-modal"
            style={{ background: "white", width: "100%", maxWidth: 760, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#8e24aa", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>+ Nuovo modello email</strong>
              <button onClick={() => setCreating(null)} data-testid="mm-email-create-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>ID *</label>
                  <input value={creating.id} onChange={e => setCreating({ ...creating, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                    placeholder="es: sollecito_pagamento" maxLength={40} data-testid="mm-email-create-id"
                    style={{ width: "100%", padding: 8, border: "2px solid #8e24aa", borderRadius: 3, fontSize: 13, fontWeight: 600, fontFamily: "Consolas, monospace" }} />
                  <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>Solo lettere minuscole, numeri, underscore</div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>ETICHETTA / NOME *</label>
                  <input value={creating.label} onChange={e => setCreating({ ...creating, label: e.target.value })}
                    placeholder="es: Sollecito pagamento" data-testid="mm-email-create-label"
                    style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 13, fontWeight: 600 }} />
                </div>
              </div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>OGGETTO *</label>
              <input value={creating.subject} onChange={e => setCreating({ ...creating, subject: e.target.value })}
                data-testid="mm-email-create-subject"
                style={{ width: "100%", padding: 8, border: "2px solid #8e24aa", borderRadius: 3, fontSize: 13, fontWeight: 600, marginBottom: 12 }} />

              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CORPO MESSAGGIO *</label>
              <textarea value={creating.body} onChange={e => setCreating({ ...creating, body: e.target.value })} rows={12}
                data-testid="mm-email-create-body"
                style={{ width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontFamily: "Consolas, monospace", resize: "vertical", lineHeight: 1.5 }} />

              <div style={{ marginTop: 10, padding: 10, background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 3 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#0d47a1", marginBottom: 6 }}>📋 PLACEHOLDER DISPONIBILI</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {placeholders.map(ph => (
                    <button key={ph} onClick={() => inserisciPh(ph)} data-testid={`mm-email-create-ph-${ph}`}
                      style={{ padding: "3px 8px", background: "white", color: "#0d47a1", border: "1px solid #1565c0", fontFamily: "Consolas, monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                      {ph}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setCreating(null)} data-testid="mm-email-create-cancel"
                style={{ background: "white", color: "#666", border: "1px solid #ccc", padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
                Annulla
              </button>
              <button onClick={creaNuovo} disabled={saving || !creating.id || !creating.subject || !creating.body} data-testid="mm-email-create-save"
                style={{ background: (saving || !creating.id || !creating.subject || !creating.body) ? "#bbb" : "#8e24aa", color: "white", border: "none", padding: "8px 20px", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 3 }}>
                {saving ? "Creazione..." : "💾 Crea modello"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
